import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button, Card, Spinner } from '@/components/ui';
import type { ProbationBucket, ProbationCase } from '@/api/services/probation.service';
import { getHrProbationSummary, listHrProbation } from '@/api/services/hrWorkspace.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { cn } from '@/lib/cn';
import { ProbationStatusBadge } from './probation/ProbationStatusBadge';
import { ProbationCaseDialog } from './probation/ProbationCaseDialog';
import type { HrAreaProps } from './hrShared';
import {
  BUCKETS,
  daysUntil,
  formatDate,
  isActionable,
  normalizeCase,
  normalizeSummary,
  relativeEnd,
} from './probation/probationFormat';

const PAGE_SIZE = 25;

/**
 * Bucket counts double as the list filter. Overdue is always first and turns
 * red once it holds anything, because a lapsed probation is the one state that
 * needs someone to act today.
 */
function BucketCards({
  counts,
  isLoading,
  selected,
  onSelect,
}: {
  counts: Record<ProbationBucket, number>;
  isLoading: boolean;
  selected: ProbationBucket | null;
  onSelect: (bucket: ProbationBucket | null) => void;
}): React.ReactElement {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      {BUCKETS.map(({ value, label, hint }) => {
        const count = counts[value];
        const isSelected = selected === value;
        const alarming = value === 'overdue' && count > 0;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(isSelected ? null : value)}
            className={cn(
              'flex min-w-0 flex-col items-start rounded-xl border px-3 py-2.5 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary',
              alarming
                ? 'border-red-300 bg-red-50 hover:bg-red-100 dark:border-red-500/40 dark:bg-red-500/10 dark:hover:bg-red-500/15'
                : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40',
              isSelected && 'ring-2 ring-primary/40'
            )}
          >
            <span
              className={cn(
                'flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide',
                alarming ? 'text-red-800 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'
              )}
            >
              {alarming && <AlertTriangle className="h-3.5 w-3.5" aria-hidden />}
              {label}
            </span>
            <span
              className={cn(
                'mt-1 text-2xl font-semibold tabular-nums',
                alarming ? 'text-red-800 dark:text-red-200' : 'text-gray-900 dark:text-gray-100'
              )}
            >
              {isLoading ? '–' : count}
            </span>
            <span className="mt-0.5 truncate text-xs text-gray-600 dark:text-gray-300">{hint}</span>
          </button>
        );
      })}
    </div>
  );
}

function EndDateCell({ probationCase }: { probationCase: ProbationCase }): React.ReactElement {
  const days = daysUntil(probationCase.currentEndDate);
  const relative = isActionable(probationCase.status) ? relativeEnd(days) : null;
  const overdue = relative !== null && days !== null && days < 0;
  return (
    <div>
      <span className="whitespace-nowrap text-gray-700 dark:text-gray-200">
        {formatDate(probationCase.currentEndDate)}
      </span>
      {relative && (
        <span
          className={cn(
            'flex items-center gap-1 text-xs',
            overdue ? 'font-medium text-danger' : 'text-gray-600 dark:text-gray-300'
          )}
        >
          {overdue && <AlertTriangle className="h-3 w-3" aria-hidden />}
          {relative}
        </span>
      )}
    </div>
  );
}

/** Probation counts across companies, a filtered case list, and the case dialog. */
export function ProbationTab({ companyIds, showCompany }: HrAreaProps): React.ReactElement {
  const [searchParams] = useSearchParams();
  const [bucket, setBucket] = useState<ProbationBucket | null>(() => {
    const requested = searchParams.get('bucket');
    return BUCKETS.some((item) => item.value === requested)
      ? (requested as ProbationBucket)
      : null;
  });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<{ id: string; companyId: string } | null>(null);
  const companyKey = companyIds?.join(',') ?? '';

  const summary = useQuery({
    queryKey: ['hr-probation-summary', companyKey],
    queryFn: () => getHrProbationSummary(companyIds),
  });
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['hr-probation-cases', companyKey, bucket, page],
    queryFn: () =>
      listHrProbation(companyIds, { bucket: bucket ?? undefined, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const counts = normalizeSummary(summary.data);
  const rows = (Array.isArray(data?.data) ? data.data : [])
    .map(normalizeCase)
    .filter((row): row is ProbationCase => row !== null);
  const total = typeof data?.total === 'number' ? data.total : rows.length;
  const limit = typeof data?.limit === 'number' && data.limit > 0 ? data.limit : PAGE_SIZE;
  const hasMore = page * limit < total;
  const bucketLabel = BUCKETS.find((item) => item.value === bucket)?.label;

  return (
    <Card contentClassName="space-y-4">
      {summary.error && (
        <p className="text-sm text-danger" role="alert">
          Counts unavailable: {getErrorMessage(summary.error)}
        </p>
      )}
      <BucketCards
        counts={counts}
        isLoading={summary.isLoading}
        selected={bucket}
        onSelect={(next) => {
          setBucket(next);
          setPage(1);
        }}
      />

      <div aria-live="polite">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <span>{bucketLabel ? `Showing ${bucketLabel.toLowerCase()} cases` : 'Showing all cases'}</span>
          {bucket && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setBucket(null);
                setPage(1);
              }}
            >
              Show all
            </Button>
          )}
        </div>
        {isLoading ? (
          <div className="flex justify-center py-10" role="status">
            <Spinner />
            <span className="sr-only">Loading probation cases</span>
          </div>
        ) : error ? (
          <div className="space-y-3" role="alert">
            <p className="text-sm text-danger">{getErrorMessage(error)}</p>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {bucket
              ? 'No probation case is in this bucket.'
              : 'No employee is on probation in this company.'}
          </p>
        ) : (
          <>
            {/* `relative` keeps the sr-only header inside the scroll box, not the page. */}
            <div className="relative overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-3 py-2">Employee</th>
                    {showCompany && (
                      <th scope="col" className="hidden px-3 py-2 lg:table-cell">Company</th>
                    )}
                    <th scope="col" className="px-3 py-2">End date</th>
                    <th scope="col" className="hidden px-3 py-2 sm:table-cell">Status</th>
                    <th scope="col" className="hidden px-3 py-2 lg:table-cell">Extensions</th>
                    <th scope="col" className="hidden px-3 py-2 md:table-cell">Review</th>
                    <th scope="col" className="px-3 py-2">
                      <span className="sr-only">Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((row) => {
                    const overdue = row.bucket === 'overdue';
                    return (
                      <tr
                        key={row._id}
                        data-overdue={overdue || undefined}
                        className={cn(overdue && 'bg-red-50/60 dark:bg-red-500/5')}
                      >
                        <td
                          className={cn(
                            'px-3 py-3',
                            overdue && 'border-l-2 border-l-red-500 dark:border-l-red-400'
                          )}
                        >
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {row.employee.name}
                          </p>
                          {row.employee.email && (
                            <p className="text-xs text-gray-600 dark:text-gray-300">
                              {row.employee.email}
                            </p>
                          )}
                        </td>
                        {showCompany && (
                          <td className="hidden px-3 py-3 text-gray-700 dark:text-gray-200 lg:table-cell">
                            {row.companyName || '—'}
                          </td>
                        )}
                        <td className="px-3 py-3">
                          <EndDateCell probationCase={row} />
                          <span className="mt-1 block sm:hidden">
                            <ProbationStatusBadge status={row.status} />
                          </span>
                        </td>
                        <td className="hidden px-3 py-3 sm:table-cell">
                          <ProbationStatusBadge status={row.status} />
                        </td>
                        <td className="hidden px-3 py-3 tabular-nums text-gray-700 dark:text-gray-200 lg:table-cell">
                          {row.extensions.length}
                        </td>
                        <td className="hidden px-3 py-3 text-gray-700 dark:text-gray-200 md:table-cell">
                          {row.review ? 'Submitted' : 'Pending'}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Button
                            variant="secondary"
                            size="sm"
                            aria-label={`View probation case for ${row.employee.name}`}
                            onClick={() => setSelected({ id: row._id, companyId: row.companyId })}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 1 || isFetching}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasMore || isFetching}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </Button>
              <span className="text-xs text-gray-600 dark:text-gray-300">
                {total} case{total === 1 ? '' : 's'}
              </span>
            </div>
          </>
        )}
      </div>

      {selected && (
        <ProbationCaseDialog
          key={selected.id}
          companyId={selected.companyId}
          caseId={selected.id}
          onClose={() => setSelected(null)}
        />
      )}
    </Card>
  );
}
